import React from "react";

// Types
import RegistrationStage from "../../../types/RegistrationStage";

// Services
import ApiService from "../../../services/ApiService";

// Components
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IonCol, IonContent, IonLoading, IonRow, withIonLifeCycle } from "@ionic/react";
import { Button } from "../../../components/Button";
import { InputFactory } from "../../../components/Factories/Input";
import { MembersTable, MembersTableFilters } from "../../../components/Members";
import { AdminTemplate } from "../../../components/PageTemplates";
import { Pagination } from "../../../components/Pagination";

// Styles
import { xor } from "lodash";
import { useTranslation, withTranslation } from "react-i18next";
import { MemberInterface } from "../../../types";
import "./Members.scss";

class AdminMembersIndex extends React.Component<any, any> {
  constructor(props: any) {
    super(props);

    this.state = {
      loading: true,
      users: [],
      page: this.props.match.params.hasOwnProperty("page") ? parseInt(this.props.match.params.page) : 1,
      registrationStages: [],
      showFilters: false,
      currentPage: 1,
      totalPages: null,
      filterFormat: null,
      totalItems: null,
      orderFilters: { desc: [], asc: [] },
      activeFilters: {},
    };
  }

  render() {
    const { t } = this.props;
    return (
      <AdminTemplate className="page-admin--members-list">
        <IonContent>
          <IonLoading isOpen={this.state.loading} message="Loading" />
          {!this.state.loading && (
            <>
              <MembersTableFilters
                registrationStages={this.state.registrationStages}
                showFilters={this.state.showFilters}
                activeFilters={this.state.activeFilters}
                toggleShowFilters={this.toggleShowFilters}
                handleClick={this.handleFilterClick}
              />
              <div className="wrapper members-table__wrapper">
                <IonRow className="members-table__header">
                  <IonCol sizeLg="4" sizeSm="12">
                    <h1>{t("member")}s</h1>
                  </IonCol>
                  <IonCol size="8">
                    <InputFactory
                      name="search"
                      type="search"
                      value={this.state.activeFilters.search}
                      placeholder={t("search")}
                      title={t("search")}
                      className="input__input input-factory__input--type-search members-table__search"
                      onIonChange={(e: any) => this.handleSearchChange(e.detail.value || null)}
                    />
                    <Button
                      text={
                        <>
                          <FontAwesomeIcon icon={["far", "filter"]} /> {t("filter")}
                        </>
                      }
                      onClick={this.toggleShowFilters}
                      className="button members-table__filter-toggle"
                    />
                  </IonCol>
                </IonRow>
                <PaginationRow state={this.state} />
                <MembersTable handleSort={this.handleToggleClick} members={this.state.users} registrationStages={this.state.registrationStages} />
                <PaginationRow state={this.state} />
              </div>
            </>
          )}
        </IonContent>
      </AdminTemplate>
    );
  }

  ionViewWillEnter() {
    const { activeFilters } = this.state;
    const { t } = this.props;

    const urlParams = new URLSearchParams(this.props.location.search);
    urlParams.forEach(function (value, key) {
      if (key.startsWith("filters")) {
        key = key.replace(/filters|[[\]]+/g, "");
        activeFilters[key] = value;
      }
    });

    this.setState({ loading: true, users: [], activeFilters });

    Promise.all([this.getRegistrationStages(), this.getMembers(activeFilters, false).catch(() => alert(t("filterUsersError")))]).then(() => this.setState({ loading: false }));
  }

  getRegistrationStages = () => {
    const { t } = this.props;
    const step = t("step");

    ApiService.get(`register`).then((registrationStagesRaw) => {
      const registrationStages: object[] = [];

      registrationStagesRaw.map(
        (stage: RegistrationStage) =>
          (registrationStages[stage.id] = {
            id: stage.id,
            icon: stage.icon,
            title: [`${step} `, stage.id, " - ", stage.name],
          }),
      );

      this.setState({ registrationStages: registrationStages.filter(Boolean) });
    });
  };

  getMembers = (options: { [key: string]: string | null } = {}, append: boolean = true) => {
    return ApiService.get("members", options)
      .then((res: { data: MemberInterface[]; current_page: number; last_page: number; total: number }) => {
        this.setState({
          users: append ? [...this.state.users, ...res.data] : res.data,
          page: res.current_page,
          last_page: res.last_page,
          totalPages: res.last_page,
          totalItems: res.total,
          currentPage: res.current_page,
        });
      })
      .catch((err) => console.error(err));
  };

  toggleShowFilters = () => {
    this.setState({ showFilters: !this.state.showFilters });
  };

  handleFilterClick = (e: any, key: string, value: any, selected: boolean) => {
    const { activeFilters } = this.state;
    const { t } = this.props;

    if (selected && value) {
      if (activeFilters[key]) {
        const currentValues = activeFilters[key].split(",");
        currentValues.push(value);
        activeFilters[key] = currentValues.join(",");
      } else {
        activeFilters[key] = value;
      }
    } else {
      if (activeFilters[key]) {
        const currentValues = activeFilters[key].split(",").filter((item: string | number) => item !== value);
        if (currentValues.length) {
          activeFilters[key] = currentValues.join(",");
        } else {
          delete activeFilters[key];
        }
      }
    }

    this.setState({ activeFilters, page: 1, last_page: 1 }, () => {
      this.getMembers(activeFilters, false).catch(() => alert(t("filterUsersError")));
    });
  };

  handleToggleClick = (key: string, direction: any) => {
    let { orderFilters, activeFilters } = this.state;
    const { t } = this.props;

    // Clear sorting on all other columns
    Object.keys(orderFilters).forEach((k) => (orderFilters[k] = []));

    if (direction) orderFilters[direction] = xor(orderFilters[direction], [key]);

    // Reset active state for all table-sort elements
    const tableSortElements = document.querySelectorAll(`.table-sort:not([data-field="${key}"])`);
    tableSortElements.forEach((element) => {
      element.classList.remove("table-sort--up", "table-sort--down");
    });

    this.setState({ orderFilters }, () => {
      orderFilters["desc"].length ? (activeFilters.orderByDesc = orderFilters["desc"].join(",")) : delete activeFilters["orderByDesc"];
      orderFilters["asc"].length ? (activeFilters.orderByAsc = orderFilters["asc"].join(",")) : delete activeFilters["orderByAsc"];

      this.setState({ activeFilters, page: 1, last_page: 1 }, () => {
        this.getMembers(activeFilters, false).catch(() => alert(t("filterUsersError")));
      });
    });
  };

  handleSearchChange = (value: string | null) => {
    const { activeFilters } = this.state;
    const { t } = this.props;

    if (value) {
      activeFilters["search"] = value;
    } else if (activeFilters["search"]) {
      delete activeFilters["search"];
    }

    this.setState({ activeFilters, page: 1, last_page: 1 }, () => {
      this.getMembers(activeFilters, false).catch(() => alert(t("filterUsersError")));
    });
  };
}

const firstHoc = withIonLifeCycle(AdminMembersIndex);
export default withTranslation()(firstHoc);

const PaginationRow: React.FC<{ state: any }> = ({ state }) => {
  const { users, currentPage, totalPages, filterFormat } = state;
  const { t } = useTranslation();

  return (
    <IonRow className="news-latest__pagination">
      <IonCol size="6">
        <span aria-label="results count">
          {t("showing")} {users?.length || 0} {t("resultsOf")} {state.totalItems || 0}
        </span>
      </IonCol>
      <IonCol size="6">
        <Pagination currentPage={currentPage} totalPages={totalPages} filterFormat={filterFormat} baseUrl="/admin/members/" />
      </IonCol>
    </IonRow>
  );
};
